// pages/HomePage.jsx - COMPLETE FILE WITH CORRECT IMPORTS
// Updated: January 5, 2026 - Added Platform Health Section (6.C4)
// Updated: January 15, 2026 - B33 Fix: Proper location detection (not hardcoded Sacramento)
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery, gql } from '@apollo/client';
import { GET_ALL_PRODUCTS } from '../apollo/queries';

import { 
  Search, 
  Store, 
  TrendingUp, 
  MapPin, 
  EyeOff, 
  Eye, 
  Plus, 
  Users, 
  ShoppingCart, 
  ChevronDown, 
  ChevronUp, 
  Coins,
  Shield,
  Star,
  CheckCircle,
  ExternalLink,
  ArrowUp,
  Loader
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';

import BottomNav from '../components/common/BottomNav';
import { ProductCard } from '../components/products';
import SearchBar from '../components/common/SearchBar';
import NotificationBell from '../components/common/NotificationBell';
import SavingsCalculator from '../components/home/SavingsCalculator';
import SkillsMonetizer from '../components/skills/SkillsMonetizer';
import SuccessStories from '../components/stories/SuccessStories';
import AnalyticsDashboard from '../components/analytics/AnalyticsDashboard';
import { useNotification } from '../context/NotificationContext';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useTokens } from '../context/TokenContext';
import { formatTokens } from '../utils/tokenEconomy';
import Tooltip from '../components/common/Tooltip';



// GraphQL Query for user analytics (real-time data)
const GET_USER_ANALYTICS = gql`
  query GetUserAnalytics($timeRange: String) {
    userAnalytics(timeRange: $timeRange) {
      lifetimeSaved
      monthSaved
      lifetimeTokensEarned
      lifetimeJobsCompleted
      groupsJoined
      tokenBalance
      trustScore
      totalReferrals
      completionRate
    }
  }
`;

// GraphQL Query for platform accountability (6.C4)
const GET_PLATFORM_ACCOUNTABILITY = gql`
  query PlatformAccountability {
    platformAccountability {
      platformTrustScore
      uptime
      activeUsersThisWeek
      activeUsersThisMonth
      jobCompletionRate
    }
  }
`;

// GraphQL Query for current user with location (B33)
const GET_USER_LOCATION = gql`
  query GetUserLocation {
    me {
      id
      location {
        city
        state
        zipCode
        coordinates {
          latitude
          longitude
        }
      }
    }
  }
`;

const HomePage = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const { getCartTotals } = useCart();
  const { user } = useAuth();
  const { balance } = useTokens();
  
  const [activeCategory, setActiveCategory] = useState('All');
  const [joinedGroups, setJoinedGroups] = useState(() => {
    // Load joined groups from localStorage
    const saved = localStorage.getItem('joinedGroups');
    return saved ? JSON.parse(saved) : [];
  });
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(true);
  const [showEndedGroups, setShowEndedGroups] = useState(true);
  const [showingSimilarTo, setShowingSimilarTo] = useState(null);
  
  // Track scroll position for floating cart animation
  const [showFloatingCart, setShowFloatingCart] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  
  // Collapsible states for value discovery sections - Added platformHealth
  const [collapsedSections, setCollapsedSections] = useState(() => {
    const saved = localStorage.getItem('homepage_collapsed_sections');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Ensure platformHealth exists in saved state
      if (parsed.platformHealth === undefined) {
        parsed.platformHealth = false;
      }
      return parsed;
    }
    return {
      platformHealth: false,  // Keep Platform Health OPEN (important info)
      savings: true,          // Collapsed by default
      skills: true,           // Collapsed by default
      analytics: true,        // Collapsed by default
      stories: true           // Collapsed by default
    };
  });

  // B33 FIX: Fetch user's stored location from profile
  const { data: userLocationData } = useQuery(GET_USER_LOCATION, {
    skip: !user,
    fetchPolicy: 'cache-first',
    errorPolicy: 'ignore'
  });
  
  // Fetch REAL user analytics from GraphQL
  const { data: analyticsData, loading: analyticsLoading } = useQuery(GET_USER_ANALYTICS, {
    variables: { timeRange: 'month' },
    skip: !user, // Skip if not logged in
    fetchPolicy: 'cache-and-network',
    pollInterval: 60000 // Refresh every 60 seconds
  });

  // Fetch platform accountability metrics (6.C4)
  const { data: platformData, loading: platformLoading } = useQuery(GET_PLATFORM_ACCOUNTABILITY, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 300000 // Refresh every 5 minutes
  });

  // Real user analytics data from GraphQL
  const userAnalytics = useMemo(() => {
    if (analyticsData?.userAnalytics) {
      return {
        totalSaved: analyticsData.userAnalytics.lifetimeSaved || 0,
        monthSaved: analyticsData.userAnalytics.monthSaved || 0,
        groupsJoined: analyticsData.userAnalytics.groupsJoined || 0,
        tokensEarned: analyticsData.userAnalytics.lifetimeTokensEarned || 0,
        jobsCompleted: analyticsData.userAnalytics.lifetimeJobsCompleted || 0,
        tokenBalance: analyticsData.userAnalytics.tokenBalance || 0,
        trustScore: analyticsData.userAnalytics.trustScore || 0,
        totalReferrals: analyticsData.userAnalytics.totalReferrals || 0,
        completionRate: analyticsData.userAnalytics.completionRate || 100
      };
    }
    // Default values when no data or loading
    return {
      totalSaved: 0,
      monthSaved: 0,
      groupsJoined: joinedGroups.length,
      tokensEarned: balance || 0,
      jobsCompleted: 0,
      tokenBalance: balance || 0,
      trustScore: 0,
      totalReferrals: 0,
      completionRate: 100
    };
  }, [analyticsData, joinedGroups.length, balance]);

  // Platform metrics from GraphQL (6.C4)
  const platformMetrics = platformData?.platformAccountability;
  const trustScore = platformMetrics?.platformTrustScore || 0;

  // ============================================
  // B33 FIX: PROPER LOCATION DETECTION
  // Priority: 1) User profile, 2) Browser geo + reverse geocode, 3) IP fallback
  // ============================================
  
  // Helper: Reverse geocode coordinates using backend API
  const reverseGeocode = useCallback(async (lat, lng) => {
    try {
      // Get base API URL (remove /graphql or /api suffix if present)
      const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/(graphql|api)$/i, '');
      const response = await fetch(`${apiUrl}/api/geo/reverse`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lat, lng })
      });
      
      if (!response.ok) {
        throw new Error('Reverse geocode failed');
      }
      
      const data = await response.json();
      return data.success ? data.location : null;
    } catch (error) {
      console.warn('[LOCATION] Reverse geocode error:', error);
      return null;
    }
  }, []);
  
  // Helper: Get location from IP via backend API
  const getLocationFromIP = useCallback(async () => {
    try {
      // Get base API URL (remove /graphql or /api suffix if present)
      const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/(graphql|api)$/i, '');
      const response = await fetch(`${apiUrl}/api/geo/location`);
      
      if (!response.ok) {
        throw new Error('IP location failed');
      }
      
      const data = await response.json();
      return data.success ? data.location : null;
    } catch (error) {
      console.warn('[LOCATION] IP geolocation error:', error);
      return null;
    }
  }, []);

  // Main location detection effect
  useEffect(() => {
    const detectLocation = async () => {
      setLocationLoading(true);
      
      // PRIORITY 1: Use authenticated user's stored location
      if (user && userLocationData?.me?.location?.city) {
        const loc = userLocationData.me.location;
        console.log('[LOCATION] Using user profile location:', loc.city);
        setUserLocation({
          city: loc.city,
          state: loc.state,
          country: loc.country || 'USA',
          coordinates: loc.coordinates ? {
            lat: loc.coordinates.latitude,
            lng: loc.coordinates.longitude
          } : null,
          source: 'profile',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        });
        setLocationLoading(false);
        return;
      }
      
      // PRIORITY 2: Browser geolocation + reverse geocode
      if (navigator.geolocation) {
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: false,
              timeout: 5000,
              maximumAge: 300000 // Cache for 5 minutes
            });
          });
          
          const { latitude, longitude } = position.coords;
          console.log('[LOCATION] Browser geolocation:', latitude, longitude);
          
          // Reverse geocode to get city name
          const geoLocation = await reverseGeocode(latitude, longitude);
          
          if (geoLocation?.city) {
            console.log('[LOCATION] Reverse geocoded city:', geoLocation.city);
            setUserLocation({
              city: geoLocation.city,
              state: geoLocation.state,
              country: geoLocation.country || 'USA',
              coordinates: { lat: latitude, lng: longitude },
              source: 'browser',
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
            });
            setLocationLoading(false);
            return;
          }
        } catch (geoError) {
          console.warn('[LOCATION] Browser geolocation denied or failed:', geoError.message);
        }
      }
      
      // PRIORITY 3: Fallback to IP-based geolocation
      console.log('[LOCATION] Falling back to IP geolocation');
      const ipLocation = await getLocationFromIP();
      
      if (ipLocation?.city && !ipLocation.isDefault) {
        console.log('[LOCATION] IP location:', ipLocation.city);
        setUserLocation({
          city: ipLocation.city,
          state: ipLocation.state,
          country: ipLocation.country || 'USA',
          coordinates: ipLocation.coordinates,
          source: 'ip',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        });
      } else {
        // Final fallback: Show "Your Area" instead of hardcoded city
        console.log('[LOCATION] Using generic fallback');
        setUserLocation({
          city: 'Your Area',
          country: 'USA',
          source: 'fallback',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        });
      }
      
      setLocationLoading(false);
    };
    
    detectLocation();
  }, [user, userLocationData, reverseGeocode, getLocationFromIP]);
  // END B33 FIX
  
  // Toggle section collapse
  const toggleSection = (section) => {
    const newState = {
      ...collapsedSections,
      [section]: !collapsedSections[section]
    };
    setCollapsedSections(newState);
    localStorage.setItem('homepage_collapsed_sections', JSON.stringify(newState));
  };
  
  // Show floating cart after scrolling down 300px
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setShowFloatingCart(scrollY > 300);
      setShowBackToTop(scrollY > 500);
    };

    
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Listen for group leave events
  useEffect(() => {
    const handleGroupLeft = () => {
      const saved = localStorage.getItem('joinedGroups');
      setJoinedGroups(saved ? JSON.parse(saved) : []);
    };
    
    window.addEventListener('groupLeft', handleGroupLeft);
    return () => window.removeEventListener('groupLeft', handleGroupLeft);
  }, []);
  
  const categories = ['Groceries', 'Household', 'Personal Care', 'Baby', 'Electronics', 'Home & Garden', 'Health & Wellness', 'Arts & Crafts'];

  // Dynamic completion detection function
  const isGroupCompleted = (group) => {
    const now = new Date();
    
    if (group.isCompleted) return true;
    if (group.progress >= 100) return true;
    if (group.currentUnits && group.nextBreakCount && group.currentUnits >= group.nextBreakCount) return true;
    if (group.endDate && group.endDate <= now) return true;
    
    return false;
  };

  // Fetch real products from backend
  const { data: productsData, loading: productsLoading, error: productsError } = useQuery(GET_ALL_PRODUCTS, {
    variables: { limit: 50 },
    fetchPolicy: 'cache-and-network'
  });

  // Transform products into group format for GroupCard compatibility
  const allGroups = useMemo(() => {
    if (!productsData?.products) return [];
    
    return productsData.products.map(product => {
      // Calculate savings based on price tiers
      const basePrice = product.basePrice || product.currentPrice || 0;
      const groupPrice = product.currentPrice || basePrice;
      const savingsPercent = basePrice > 0 ? Math.round((1 - groupPrice / basePrice) * 100) : 15;
      
      // Create end date (7 days from now for active products)
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + 7);
      
      return {
        id: product.id,
        title: product.name,
        description: product.description || '',
        category: product.category || 'Other',
        images: product.images || [],
        image: product.images?.[0]?.full || product.images?.[0] || '/placeholder-product.jpg',
        currentPrice: product.currentPrice || basePrice,
        originalPrice: basePrice,
        savings: savingsPercent,
        savingsAmount: basePrice - (product.currentPrice || groupPrice),
        members: product.groups?.[0]?.currentSize || 0,
        maxMembers: product.groups?.[0]?.targetSize || 50,
        endDate: endDate,
        vendor: product.vendor?.name || 'Local Vendor',
        location: product.vendor?.location || 'Sacramento, CA',
        unit: product.unit || 'each',
        quantity: product.quantity || 1,
        available: product.available !== false,
        organic: product.attributes?.organic || false,
        local: product.attributes?.local || false,
        sustainable: product.attributes?.sustainablySourced || false,
        priceTiers: product.priceTiers || []
      };
    });
  }, [productsData]);

  // Real-time countdown updates
  useEffect(() => {
    const updateFrequency = () => {
      const hasUrgentGroup = allGroups.some(group => {
        if (isGroupCompleted(group)) return false;
        const now = new Date();
        const difference = group.endDate - now;
        const hoursLeft = difference / (1000 * 60 * 60);
        return hoursLeft <= 1 && hoursLeft > 0;
      });
      
      return hasUrgentGroup ? 1000 : 60000;
    };
    
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, updateFrequency());

    return () => clearInterval(timer);
  }, [allGroups]);

  // Filter groups based on category and ended status
  useEffect(() => {
    let filtered = allGroups;
    
    if (activeCategory !== 'All') {
      filtered = filtered.filter(group => group.category === activeCategory);
    }
    
    if (!showEndedGroups) {
      filtered = filtered.filter(group => !isGroupCompleted(group));
    }
    
    setFilteredGroups(filtered);
  }, [allGroups, activeCategory, showEndedGroups, currentTime]);

  const handleGroupJoined = (groupId) => {
    const newJoinedGroups = [...joinedGroups, groupId];
    setJoinedGroups(newJoinedGroups);
    
    // Save to localStorage
    localStorage.setItem('joinedGroups', JSON.stringify(newJoinedGroups));
    
    // Dispatch custom event so GroupsPage updates
    window.dispatchEvent(new Event('groupJoined'));
  };

  // Handle finding similar groups
  const handleFindSimilarGroups = (completedGroup, e) => {
    e.stopPropagation();
    
    // Show user feedback
    addNotification({
      type: 'info',
      title: 'Finding Similar Groups',
      message: `Looking for groups similar to ${completedGroup.title}...`,
      showToast: true
    });
    
    // Set similar group context and filter to category
    setShowingSimilarTo(completedGroup);
    setActiveCategory(completedGroup.category);
    
    // Scroll to top to show new groups
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Reset similar groups view
  const handleResetView = () => {
    setShowingSimilarTo(null);
    setActiveCategory('All');
  };

  // Handle "Create Your Own" navigation
  const handleCreateYourOwn = (completedGroup, e) => {
    e.stopPropagation();
    
    // Navigate to create group page with context
    navigate('/create-group', { 
      state: { 
        similarTo: completedGroup,
        category: completedGroup.category 
      }
    });
  };
  
  const handleCategoryClick = (category) => {
    setActiveCategory(category);
    // Reset similar groups view when manually changing category
    if (showingSimilarTo && category !== showingSimilarTo.category) {
      setShowingSimilarTo(null);
    }
  };

  // Calculate counts
  const endedGroupsCount = allGroups.filter(group => isGroupCompleted(group)).length;
  const actualUserCreatedGroups = allGroups.filter(group => group.isUserCreated);
  const userCreatedGroupsCount = Math.min(actualUserCreatedGroups.length, 2);
  const similarGroupsCount = showingSimilarTo ? allGroups.filter(group => group.isSimilarGroup).length : 0;

  // Get cart totals for floating button
  const cartTotals = getCartTotals();

  // Get trust score color (6.C4)
  const getTrustScoreColor = (score) => {
    if (score >= 4.5) return { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-600' };
    if (score >= 4.0) return { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-600' };
    if (score >= 3.5) return { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-600' };
    return { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-600' };
  };

  const trustScoreColors = getTrustScoreColor(trustScore);

  // Collapsible Section Component
  const CollapsibleSection = ({ title, isCollapsed, onToggle, children, icon }) => (
    <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200">
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="font-semibold text-lg">{title}</h3>
        </div>
        {isCollapsed ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
      </button>
      {!isCollapsed && (
        <div className="px-4 pb-4">
          {children}
        </div>
      )}
    </div>
  );

  // B33 FIX: Format location display
  const formatLocationDisplay = () => {
    if (locationLoading) {
      return (
        <span className="flex items-center gap-1">
          <Loader size={12} className="animate-spin" />
          Detecting location...
        </span>
      );
    }
    
    if (!userLocation) {
      return 'Showing products near you';
    }
    
    const { city, state, country } = userLocation;
    
    if (city === 'Your Area') {
      return 'Showing products near you';
    }
    
    // Format: "Sacramento, CA" or "Sacramento, USA" if no state
    const locationParts = [city];
    if (state) {
      locationParts.push(state);
    } else if (country && country !== 'USA') {
      locationParts.push(country);
    }
    
    return `Showing products in ${locationParts.join(', ')}`;
  };

  return (
    <div className="container mx-auto px-4 pb-20">
      
      <div className="flex justify-between items-center mt-6 mb-4">
        <h1 className="text-2xl font-bold">Deals Near You</h1>
        <div className="flex items-center">
          <button 
            onClick={() => navigate('/cart')}
            className="relative mr-3 p-2 hover:bg-gray-100 rounded-full"
          >
            <ShoppingCart size={20} />
            {cartTotals.totalItems > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {cartTotals.totalItems}
              </span>
            )}
          </button>
          <NotificationBell />
          <button 
            onClick={() => navigate('/wallet')} 
            className="flex items-center gap-1 bg-yellow-50 px-2 py-1 rounded-full ml-3"
          >
            <Coins className="w-4 h-4 text-yellow-600" />
            <span className="text-sm font-medium text-yellow-700">
              {formatTokens(balance || 0)}
            </span>
          </button>
        </div>
      </div>

      {/* B33 FIX: Location Display - Now shows real detected location */}
      <div className="flex items-center mb-4 text-sm text-gray-600">
        <MapPin size={16} className="mr-1 flex-shrink-0" />
        <span>{formatLocationDisplay()}</span>
        {userLocation?.source === 'profile' && (
          <span className="ml-2 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
            ✓ Saved
          </span>
        )}
      </div>

      {/* Similar Groups Context Banner */}
      {showingSimilarTo && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="bg-blue-100 rounded-full p-2 mr-3">
                <Search size={20} className="text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-blue-800">Similar Groups Found!</h3>
                <p className="text-sm text-blue-600">
                  Based on your interest in "{showingSimilarTo.title}" - showing {similarGroupsCount} similar options
                </p>
              </div>
            </div>
            <button
              onClick={handleResetView}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              View All Groups
            </button>
          </div>
        </div>
      )}
      
      {/* Search Bar */}
      <SearchBar className="mb-4" />
      
      {/* Category Filters */}
      <div className="flex gap-2 overflow-x-auto py-2 mb-4">
        <div 
          className={`px-3 py-1 rounded-full whitespace-nowrap cursor-pointer ${
            activeCategory === 'All' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
          }`}
          onClick={() => handleCategoryClick('All')}
        >
          All
        </div>
        {categories.map((category) => (
          <div 
            key={category}
            className={`px-3 py-1 rounded-full whitespace-nowrap cursor-pointer ${
              activeCategory === category ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
            }`}
            onClick={() => handleCategoryClick(category)}
          >
            {category}
          </div>
        ))}
      </div>

      {/* Platform Activity Summary */}
      <div className="flex gap-2 mb-4 text-xs">
        {userCreatedGroupsCount > 0 && (
          <div className="bg-purple-50 text-purple-700 px-2 py-1 rounded-full flex items-center">
            <Users size={12} className="mr-1" />
            {userCreatedGroupsCount} group{userCreatedGroupsCount !== 1 ? 's' : ''} you created
          </div>
        )}
        {endedGroupsCount > 0 && (
          <div className="bg-green-50 text-green-700 px-2 py-1 rounded-full">
            {endedGroupsCount} completed group{endedGroupsCount !== 1 ? 's' : ''}
          </div>
        )}
        {similarGroupsCount > 0 && (
          <div className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full">
            {similarGroupsCount} similar option{similarGroupsCount !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Show/Hide Ended Groups Toggle */}
      {endedGroupsCount > 0 && (
        <div className="flex items-center justify-between mb-4 p-3 bg-gray-50 rounded-lg">
          <span className="text-sm text-gray-600">
            {endedGroupsCount} completed group{endedGroupsCount !== 1 ? 's' : ''} available
          </span>
          <button
            onClick={() => setShowEndedGroups(!showEndedGroups)}
            className="flex items-center text-sm text-blue-600 hover:text-blue-800"
          >
            {showEndedGroups ? (
              <>
                <EyeOff size={16} className="mr-1" />
                Hide Completed
              </>
            ) : (
              <>
                <Eye size={16} className="mr-1" />
                Show Completed
              </>
            )}
          </button>
        </div>
      )}

      {/* Platform Health Section (6.C4 - NEW) */}
      <CollapsibleSection
        title="Platform Health"
        icon={<Shield size={20} className="text-blue-600" />}
        isCollapsed={collapsedSections.platformHealth}
        onToggle={() => toggleSection('platformHealth')}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            We hold ourselves accountable. Here's how UniLedge is performing in real-time:
          </p>

          {platformLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 animate-pulse">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="bg-gray-100 rounded-lg p-3 h-16" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {/* Trust Score */}
              <div className={`rounded-lg p-3 border ${trustScoreColors.bg} ${trustScoreColors.border}`}>
                <div className="flex items-center gap-1 mb-1">
                  <Star size={12} className={`${trustScoreColors.text} fill-current`} />
                  <span className="text-xs font-medium text-gray-600">Trust Score</span>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className={`text-xl font-bold ${trustScoreColors.text}`}>
                    {trustScore.toFixed(1)}
                  </span>
                  <span className="text-xs text-gray-500">/5.0</span>
                </div>
              </div>

              {/* Uptime */}
              <div className="rounded-lg p-3 border border-green-200 bg-green-50">
                <div className="flex items-center gap-1 mb-1">
                  <TrendingUp size={12} className="text-green-600" />
                  <span className="text-xs font-medium text-gray-600">Uptime</span>
                  <Tooltip
                    content="Platform availability over the last 30 days. We aim for 99.9% uptime so you can always access deals and manage orders."
                    position="top"
                    size="sm"
                    variant="default"
                  />
                </div>
                <span className="text-xl font-bold text-green-600">
                  {((platformMetrics?.uptime || 0) * 100).toFixed(1)}%
                </span>
              </div>

              {/* Job Completion */}
              <div className="rounded-lg p-3 border border-blue-200 bg-blue-50">
                <div className="flex items-center gap-1 mb-1">
                  <CheckCircle size={12} className="text-blue-600" />
                  <span className="text-xs font-medium text-gray-600">Jobs Done</span>
                  <Tooltip
                    content="Percentage of community jobs completed successfully. Higher rates mean reliable workers and smooth operations."
                    position="top"
                    size="sm"
                    variant="default"
                  />
                </div>
                <span className="text-xl font-bold text-blue-600">
                  {((platformMetrics?.jobCompletionRate || 0) * 100).toFixed(0)}%
                </span>
              </div>

              {/* Active Users */}
              <div className="rounded-lg p-3 border border-purple-200 bg-purple-50">
                <div className="flex items-center gap-1 mb-1">
                  <Users size={12} className="text-purple-600" />
                  <span className="text-xs font-medium text-gray-600">Active</span>
                  <Tooltip
                    content="Members who made purchases or completed jobs this week. More active users = more group buying power!"
                    position="top"
                    size="sm"
                    variant="default"
                  />
                </div>
                <span className="text-xl font-bold text-purple-600">
                  {platformMetrics?.activeUsersThisWeek?.toLocaleString() || 0}
                </span>
              </div>
            </div>
          )}

          {/* Transparency Link */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
            <p className="text-xs text-gray-600 italic">
              "We don't just build the rules — we follow them."
            </p>
            <Link 
              to="/transparency" 
              className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
            >
              Full Report
              <ExternalLink size={12} />
            </Link>
          </div>
        </div>
      </CollapsibleSection>

      {/* Collapsible Value Discovery Sections */}
      <CollapsibleSection
        title="Calculate Your Annual Savings"
        isCollapsed={collapsedSections.savings}
        onToggle={() => toggleSection('savings')}
      >
        <SavingsCalculator />
      </CollapsibleSection>

      <CollapsibleSection
        title="Turn Your Skills Into Income"
        isCollapsed={collapsedSections.skills}
        onToggle={() => toggleSection('skills')}
      >
        <SkillsMonetizer />
      </CollapsibleSection>

      <CollapsibleSection
        title="UniLedge Community Impact"
        isCollapsed={collapsedSections.analytics}
        onToggle={() => toggleSection('analytics')}
      >
        <AnalyticsDashboard />
      </CollapsibleSection>

      <CollapsibleSection
        title="Real Member Stories"
        isCollapsed={collapsedSections.stories}
        onToggle={() => toggleSection('stories')}
      >
        <SuccessStories />
      </CollapsibleSection>

      {/* Analytics Summary - NOW WITH REAL USER DATA */}
      {user ? (
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 mb-4 border border-blue-100">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold text-blue-800">Your UniLedge Stats</h3>
            <button 
              onClick={() => navigate('/analytics')}
              className="text-sm text-blue-600 flex items-center hover:text-blue-800"
            >
              <span>View Details</span>
              <TrendingUp size={14} className="ml-1" />
            </button>
          </div>
          
          {/* Top Row - Key Metrics */}
          <div className="grid grid-cols-4 gap-2 mb-3">
            <div className="bg-white rounded-lg p-2 text-center shadow-sm">
              <div className="text-xs text-gray-500 mb-1">Total Saved</div>
              <div className="font-bold text-green-600">
                {analyticsLoading ? '...' : `$${userAnalytics.totalSaved.toFixed(2)}`}
              </div>
            </div>
            <div className="bg-white rounded-lg p-2 text-center shadow-sm">
              <div className="text-xs text-gray-500 mb-1">Token Balance</div>
              <div className="font-bold text-yellow-600 flex items-center justify-center">
                <Coins size={14} className="mr-1" />
                {analyticsLoading ? '...' : userAnalytics.tokenBalance}
              </div>
            </div>
            <div className="bg-white rounded-lg p-2 text-center shadow-sm">
              <div className="text-xs text-gray-500 mb-1">Trust Score</div>
              <div className="font-bold text-indigo-600 flex items-center justify-center">
                <Shield size={14} className="mr-1" />
                {analyticsLoading ? '...' : userAnalytics.trustScore.toFixed(1)}
              </div>
            </div>
            <div className="bg-white rounded-lg p-2 text-center shadow-sm">
              <div className="text-xs text-gray-500 mb-1">Jobs Done</div>
              <div className="font-bold text-purple-600">
                {analyticsLoading ? '...' : userAnalytics.jobsCompleted}
              </div>
            </div>
          </div>
          
          {/* Bottom Row - Secondary Metrics */}
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-blue-100/50 rounded-lg p-2 text-center">
              <div className="text-xs text-blue-700 mb-1">This Month</div>
              <div className="font-semibold text-blue-800">
                ${analyticsLoading ? '...' : userAnalytics.monthSaved.toFixed(2)}
              </div>
            </div>
            <div className="bg-blue-100/50 rounded-lg p-2 text-center">
              <div className="text-xs text-blue-700 mb-1">Tokens Earned</div>
              <div className="font-semibold text-blue-800">
                {analyticsLoading ? '...' : userAnalytics.tokensEarned}
              </div>
            </div>
            <div className="bg-blue-100/50 rounded-lg p-2 text-center">
              <div className="text-xs text-blue-700 mb-1">Referrals</div>
              <div className="font-semibold text-blue-800">
                {analyticsLoading ? '...' : userAnalytics.totalReferrals}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 mb-4 border border-blue-100">
          <div className="text-center">
            <h3 className="font-semibold text-blue-800 mb-2">Track Your Savings</h3>
            <p className="text-sm text-blue-600 mb-3">Sign in to see your personalized stats and savings</p>
            <button 
              onClick={() => navigate('/login')}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700"
            >
              Sign In
            </button>
          </div>
        </div>
      )}
      
      {/* Deals - Product Cards */}
      <div className="grid grid-cols-2 gap-3">
        {filteredGroups.map((product) => (
          <ProductCard
            key={product.id}
            product={product}
            compact={true}
          />
        ))}
      </div>
      {/* Create Group Suggestion */}
      {showingSimilarTo && similarGroupsCount > 0 && (
        <div className="mt-6 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-200">
          <div className="flex items-center">
            <div className="bg-purple-100 rounded-full p-2 mr-3">
              <Plus size={20} className="text-purple-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-purple-800">Don't see exactly what you want?</h3>
              <p className="text-sm text-purple-600 mb-3">
                Create your own {showingSimilarTo.category.toLowerCase()} group and become the admin!
              </p>
              <button 
                className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
                onClick={() => handleCreateYourOwn(showingSimilarTo)}
              >
                Create Your Own Group
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Vendor Banner */}
      <div className="mt-8 bg-blue-50 rounded-lg p-4 border border-blue-100">
        <div className="flex">
          <div className="mr-3">
            <Store className="text-blue-700" size={24} />
          </div>
          <div>
            <h3 className="font-semibold text-blue-800">Are you a local vendor?</h3>
            <p className="text-sm text-blue-800 opacity-80 mb-3">
              Join our platform to sell directly to community groups and grow your business.
            </p>
            <button 
              className="text-sm bg-blue-700 text-white px-3 py-1 rounded-full"
              onClick={() => navigate('/vendors')}
            >
              Learn More
            </button>
          </div>
        </div>
      </div>

      {/* Floating Cart Button - Shows after scrolling down and when cart has items */}
      {/* Positioned above AVA icon and BottomNav */}
      {cartTotals.totalItems > 0 && (
        <button
          onClick={() => navigate('/cart')}
          className={`fixed right-4 z-40 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-full shadow-lg hover:shadow-xl transform transition-all duration-300 ease-out ${
            showFloatingCart 
              ? 'bottom-44 opacity-100 scale-100 translate-y-0' 
              : 'bottom-44 opacity-0 scale-75 translate-y-4 pointer-events-none'
          }`}
          style={{ 
            width: '60px', 
            height: '60px',
            boxShadow: '0 4px 20px rgba(59, 130, 246, 0.4)'
          }}
          aria-label={`View cart with ${cartTotals.totalItems} items`}
        >
          <div className="relative flex items-center justify-center w-full h-full">
            <ShoppingCart size={24} />
            {/* Item count badge */}
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center border-2 border-white">
              {cartTotals.totalItems > 99 ? '99+' : cartTotals.totalItems}
            </span>
            {/* Subtle price indicator */}
            <span className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 bg-green-500 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">
              ${cartTotals.subtotal?.toFixed(0) || 0}
            </span>
          </div>
        </button>
      )}

      {/* Back to Top Button */}
      <button
        onClick={scrollToTop}
        className={`fixed left-4 z-40 bg-gray-800 text-white rounded-full shadow-lg hover:bg-gray-700 transform transition-all duration-300 ${
          showBackToTop 
            ? 'bottom-44 opacity-100 scale-100' 
            : 'bottom-44 opacity-0 scale-75 pointer-events-none'
        }`}
        style={{ width: '48px', height: '48px' }}
        aria-label="Back to top"
      >
        <div className="flex items-center justify-center w-full h-full">
          <ArrowUp size={20} />
        </div>
      </button>
      
    
      
      <BottomNav />
    </div>
  );
};

export default HomePage;